/**
 * Advanced Support Resistance Zones
 *
 * Port of TradingView's open-source "Advanced Support Resistance Zones"
 * by Michael_Fx_Trader (https://in.tradingview.com/script/Ew0LRhby-).
 *
 * What it does (mirrors the TradingView script):
 * - Confirmed swing highs/lows (pivot left/right bars) become zone candidates
 * - Nearby pivots of the same type MERGE into one zone via a running weighted
 *   average of touches (center converges on the average of every touch)
 * - Each zone is a band: center +/- (Zone Half-Width x ATR) — drawn as a
 *   FILLED box (translucent fill + border) from the pivot bar to the right
 *   edge, with a TV-style in-band right label: "RES 4996.275 | 2x"
 * - Touch counting: only a fresh entry into the band counts (no grind counting)
 * - Role reversal: a close fully beyond a zone flips RES <-> SUP, resets touches
 * - Max Zones Stored: farthest zone from live price is dropped first
 * - Minimum Touches filter controls which zones are displayed
 * - Exactly one "strongest" zone (highest touch count) is highlighted with a star
 * - Emits dashboard metadata for the top-right "Advanced S/R" table overlay
 *   (active zones, At Price, Nearest Above/Below in ATRs, Strongest Shelf,
 *   Structure State — same rows as the TradingView dashboard)
 *
 * Not renderable in this platform (omitted): the "zone extend N bars ahead"
 * limit (our bands extend to the right edge of the chart instead).
 *
 * Category: Support/Resistance | Pane: Overlay
 */
import { IndicatorDefinition, PlotData, IndicatorBand } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";
import { computeATR } from "./atr";

interface Zone {
  type: "res" | "sup";
  center: number;
  half: number;
  touches: number;
  createdIdx: number;
  wasInside: boolean;
}

/** Dashboard data for the top-right "Advanced S/R" info table overlay */
export interface AsrzDashboard {
  activeZones: number;
  currentPrice: number;
  /** Type of zone the current price is inside, if any */
  atPriceInside: "res" | "sup" | null;
  /** Nearest resistance center above current price + distance in ATRs */
  nearestAbove: { price: number; atr: number } | null;
  /** Nearest support center below current price + distance in ATRs */
  nearestBelow: { price: number; atr: number } | null;
  /** Zone with the highest touch count */
  strongest: { type: "R" | "S"; touches: number; price: number } | null;
  structureState: "Inside Resistance" | "Inside Support" | "Between Zones";
}

/** TV-style price formatting: up to 3 decimals, always at least 2 */
export const asrzFormatPrice = (v: number): string => {
  let s = v.toFixed(3);
  if (s.endsWith("0")) s = v.toFixed(2);
  return s;
};

export const advancedSupportResistanceZonesIndicator: IndicatorDefinition = {
  id: "advanced_support_resistance_zones",
  name: "Advanced Support Resistance Zones",
  shortName: "SR Zones",
  category: "support_resistance",
  pane: "overlay",
  params: [
    { key: "pivotLeft", label: "Pivot Left Bars", type: "number", default: 5, min: 1, max: 50 },
    { key: "pivotRight", label: "Pivot Right Bars", type: "number", default: 5, min: 1, max: 50 },
    { key: "atrLength", label: "ATR Length (for zone sizing)", type: "number", default: 14, min: 1, max: 500 },
    { key: "zoneHalfWidth", label: "Zone Half-Width (x ATR)", type: "number", default: 0.35, min: 0.01, max: 10, step: 0.01 },
    { key: "mergeTolerance", label: "Merge Tolerance (x ATR)", type: "number", default: 0.6, min: 0.05, max: 10, step: 0.05 },
    { key: "maxZones", label: "Max Zones Stored", type: "number", default: 25, min: 5, max: 100 },
    { key: "minTouches", label: "Minimum Touches to Display a Zone", type: "number", default: 1, min: 1, max: 20 },
    { key: "resZoneColor", label: "Resistance Zone Color", type: "color", default: "rgba(242,54,69,0.45)" },
    { key: "supZoneColor", label: "Support Zone Color", type: "color", default: "rgba(76,175,80,0.45)" },
    { key: "highlightColor", label: "Strongest Zone Highlight Color", type: "color", default: "rgba(253,216,53,0.65)" },
    { key: "resBorder", label: "Resistance Border Color", type: "color", default: "rgba(242,54,69,1)" },
    { key: "supBorder", label: "Support Border Color", type: "color", default: "rgba(76,175,80,1)" },
  ],
  plots: [
    { key: "zones", label: "SR Zones", color: "#4CAF50", style: "line", lastValueVisible: false, priceLineVisible: false },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const bands: IndicatorBand[] = [];
    const empty: PlotData[] = [{ key: "zones", data: [], bands }];

    const {
      pivotLeft,
      pivotRight,
      atrLength,
      zoneHalfWidth,
      mergeTolerance,
      maxZones,
      minTouches,
      resZoneColor,
      supZoneColor,
      highlightColor,
      resBorder,
      supBorder,
    } = params;

    const n = candles.length;
    if (n < 3) return empty;

    const left = Math.max(1, Math.round(pivotLeft));
    const right = Math.max(1, Math.round(pivotRight));
    const atr = computeATR(candles, Math.max(1, Math.round(atrLength)));
    const atrAt = (i: number): number => {
      if (!isNaN(atr[i])) return atr[i];
      for (let k = i; k >= 0; k--) if (!isNaN(atr[k])) return atr[k];
      return Math.max(candles[i].high - candles[i].low, 1e-9);
    };

    // ── 1. Confirmed swing highs / swing lows ─────────────────────
    const pivotHighs: { idx: number; price: number }[] = [];
    const pivotLows: { idx: number; price: number }[] = [];
    for (let i = left; i <= n - 1 - right; i++) {
      let isHigh = true;
      let isLow = true;
      for (let j = 1; j <= left && (isHigh || isLow); j++) {
        if (isHigh && (candles[i].high <= candles[i - j].high || candles[i].high <= candles[i + j].high)) isHigh = false;
        if (isLow && (candles[i].low >= candles[i - j].low || candles[i].low >= candles[i + j].low)) isLow = false;
      }
      if (isHigh) pivotHighs.push({ idx: i, price: candles[i].high });
      if (isLow) pivotLows.push({ idx: i, price: candles[i].low });
    }

    // ── 2. Walk bars: merge/create zones, count touches, flip ─────
    const zones: Zone[] = [];
    let highPtr = 0;
    let lowPtr = 0;
    const zoneHalf = (i: number) => Math.max(atrAt(i) * Number(zoneHalfWidth), 1e-9);
    const tolerance = (i: number) => atrAt(i) * Number(mergeTolerance);

    const absorb = (p: number, type: "res" | "sup", i: number, pivotIdx: number) => {
      // Merge into nearest same-type zone within tolerance (running weighted average)
      let best = -1;
      let bestDist = Infinity;
      const tol = tolerance(i);
      for (let z = 0; z < zones.length; z++) {
        if (zones[z].type !== type) continue;
        const d = Math.abs(p - zones[z].center);
        if (d <= tol && d < bestDist) {
          best = z;
          bestDist = d;
        }
      }
      if (best >= 0) {
        const z = zones[best];
        z.center = (z.center * z.touches + p) / (z.touches + 1);
        z.touches += 1;
      } else {
        zones.push({
          type,
          center: p,
          half: zoneHalf(i),
          touches: 1,
          createdIdx: pivotIdx,
          wasInside: true, // the pivot itself reacted inside the new band
        });
      }
    };

    for (let i = 0; i < n; i++) {
      // New pivots confirmed at this bar (pivot bar + right window passed)
      while (highPtr < pivotHighs.length && pivotHighs[highPtr].idx + right <= i) {
        const ph = pivotHighs[highPtr];
        absorb(ph.price, "res", i, ph.idx);
        highPtr++;
      }
      while (lowPtr < pivotLows.length && pivotLows[lowPtr].idx + right <= i) {
        const pl = pivotLows[lowPtr];
        absorb(pl.price, "sup", i, pl.idx);
        lowPtr++;
      }

      const bar = candles[i];
      for (const z of zones) {
        const top = z.center + z.half;
        const bottom = z.center - z.half;
        const interacted = bar.high >= bottom && bar.low <= top;
        if (interacted && !z.wasInside && z.createdIdx < i) z.touches++;
        z.wasInside = interacted;
        // Role reversal: a full close through the FAR side of the zone breaks
        // it — broken resistance becomes support (and vice versa)
        if (z.createdIdx < i) {
          if (z.type === "res" && bar.close > top) {
            z.type = "sup";
            z.touches = 0;
          } else if (z.type === "sup" && bar.close < bottom) {
            z.type = "res";
            z.touches = 0;
          }
        }
      }

      // Storage cap: drop the zone farthest from live price
      if (zones.length > Number(maxZones)) {
        let worst = -1;
        let worstDist = -1;
        for (let z = 0; z < zones.length; z++) {
          const d = Math.abs(zones[z].center - bar.close);
          if (d > worstDist) {
            worstDist = d;
            worst = z;
          }
        }
        if (worst >= 0) zones.splice(worst, 1);
      }
    }

    // ── 3. Strongest zone (highest touch count) ───────────────────
    const visible = zones.filter(z => z.touches >= Number(minTouches));
    let strongest: Zone | null = null;
    for (const z of visible) {
      if (!strongest || z.touches > strongest.touches) strongest = z;
    }

    // ── 4. Filled zone bands (TV-style boxes with in-band labels) ─
    visible.forEach((z, idx) => {
      const isStrongest = z === strongest;
      const isRes = z.type === "res";
      const fill = isStrongest ? highlightColor : isRes ? resZoneColor : supZoneColor;
      const border = isStrongest ? highlightColor : isRes ? resBorder : supBorder;
      const label =
        `${isStrongest ? "★ " : ""}${isRes ? "RES" : "SUP"} ` +
        `${asrzFormatPrice(z.center)} | ${z.touches}x`;
      bands.push({
        id: `asrz_${idx}`,
        top: z.center + z.half,
        bottom: z.center - z.half,
        fill,
        border,
        fromTime: candles[z.createdIdx].time,
        lineWidth: 1,
        label,
        labelColor: border,
      });
    });

    // ── 5. Dashboard data (top-right "Advanced S/R" table) ────────
    const close = candles[n - 1].close;
    const atrNow = Math.max(atrAt(n - 1), 1e-9);

    // Zone the current price sits inside (nearest center wins)
    let containing: Zone | null = null;
    let containingDist = Infinity;
    for (const z of visible) {
      const d = Math.abs(close - z.center);
      if (d <= z.half && d < containingDist) {
        containingDist = d;
        containing = z;
      }
    }

    // Nearest resistance above / support below (excluding the zone we are inside)
    let nearestAbove: { price: number; atr: number } | null = null;
    let nearestBelow: { price: number; atr: number } | null = null;
    for (const z of visible) {
      if (z === containing) continue;
      if (z.type === "res" && z.center > close) {
        if (!nearestAbove || z.center < nearestAbove.price) {
          nearestAbove = { price: z.center, atr: (z.center - close) / atrNow };
        }
      } else if (z.type === "sup" && z.center < close) {
        if (!nearestBelow || z.center > nearestBelow.price) {
          nearestBelow = { price: z.center, atr: (close - z.center) / atrNow };
        }
      }
    }

    const dashboard: AsrzDashboard = {
      activeZones: visible.length,
      currentPrice: close,
      atPriceInside: containing ? containing.type : null,
      nearestAbove,
      nearestBelow,
      strongest: strongest
        ? { type: strongest.type === "res" ? "R" : "S", touches: strongest.touches, price: strongest.center }
        : null,
      structureState: containing
        ? containing.type === "res" ? "Inside Resistance" : "Inside Support"
        : "Between Zones",
    };

    return [{
      key: "zones",
      data: [],
      bands,
      // Structured state for overlay components (dashboard table)
      metadata: {
        zones: visible.map(z => ({
          type: z.type,
          center: z.center,
          half: z.half,
          touches: z.touches,
          createdIdx: z.createdIdx,
          isStrongest: z === strongest,
        })),
        strongestPrice: strongest ? strongest.center : null,
        dashboard,
      },
    }];
  },
};
